import React, {Dispatch, SetStateAction} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import classNames from 'classnames';
import {Form} from 'formik';

import {Course, Store} from 'src/types/store';
import {saveAttendance} from 'shared/portfolio/actions/courses';

import Icon from 'shared/components/icon';
import Button from 'src/components/button';
import Checkbox from 'shared/components/checkbox';
import InfoTooltip from 'shared/components/info-tooltip';
import TextButton from 'src/components/TextButton';
import Section from 'shared/portfolio/components/section';
import CourseFormWrapper, {CourseForm} from './course-form-wrapper';
import SchoolSection from './school-section';
import EditingSchoolSection from './editing-school-section';
import styles from './school-year-section.module.scss';

interface Props {
  editing: boolean;
  setEditing: Dispatch<SetStateAction<boolean>>;
  classYear: string;
  year: string;
  initialValues: Record<string, CourseForm[]>;
  coursesByGradesAndSchool: Record<string, Record<string, Course[]>>;
}

const SchoolYearSection = ({
  editing,
  setEditing,
  classYear,
  year,
  initialValues,
  coursesByGradesAndSchool,
}: Props) => {
  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const history = useHistory();
  const attendance = useSelector((state: Store) => state.attendance);

  return (
    <Section>
      <div
        className={classNames('portfolio-section-wrapper-header', styles.bulk, {
          [styles.editing]: editing,
        })}
      >
        <div>
          {classYear} Year
          {!editing && attendance[year] === 0 && (
            <span className={styles.attendance}>
              <Icon iconType="check" className={styles.check} /> Perfect Attendance
            </span>
          )}
        </div>

        {editing ? (
          <div className={styles.editingHeader}>
            <div className={styles.editingAttendance}>
              <Checkbox
                label="Perfect attendance"
                defaultChecked={attendance[year] === 0}
                className="m-0"
                onChange={(e) => {
                  if (e.target.checked) {
                    dispatch(saveAttendance(year, 0));
                  } else {
                    dispatch(saveAttendance(year, 1));
                  }
                }}
              />
              <InfoTooltip>
                No unexcused absences or tardies where you took most of your courses this school
                year.
              </InfoTooltip>
            </div>
            <span className="ml-8 mr-12">|</span>
            <TextButton
              margin={false}
              onClick={() =>
                history.replace({
                  pathname: `${pathname}/school/new`,
                  state: {year, skipScrolling: true},
                })
              }
              testid="bulk-entry-add-school"
            >
              + Add School
            </TextButton>
          </div>
        ) : (
          <TextButton
            className="m-0"
            onClick={() => setEditing(!editing)}
            testid="bulk-entry-edit-mode"
          >
            Edit <Icon iconType="pen" className={styles.pen} />
          </TextButton>
        )}
      </div>

      <CourseFormWrapper
        initialValues={initialValues}
        onSuccess={() => {
          setEditing(false);
        }}
      >
        {(formik) => (
          <Form autoComplete="off">
            {Object.keys(coursesByGradesAndSchool).map((institutionId) => {
              const courseGroups = coursesByGradesAndSchool[institutionId];

              return editing ? (
                <EditingSchoolSection
                  key={institutionId}
                  institutionId={institutionId}
                  courseGroups={courseGroups}
                  year={year}
                />
              ) : (
                <SchoolSection
                  key={institutionId}
                  institutionId={institutionId}
                  courseGroups={courseGroups}
                  classYear={classYear}
                  onAddClick={() => setEditing(true)}
                />
              );
            })}

            {editing && (
              <div className={styles.editingFooter}>
                <Button
                  margin={false}
                  horizontalMargin
                  width={85}
                  outline
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  margin={false}
                  width={85}
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid}
                  testId="bulk-entry-save-button"
                >
                  {formik.isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </Form>
        )}
      </CourseFormWrapper>
    </Section>
  );
};

export default SchoolYearSection;
